/** Shared UI primitives for grammar-lesson activities (DESIGN.md dark-scope surfaces). */
import { useMemo } from "react";

export function PlayButton({ text, playingText, onPlay, className = "" }) {
  const playing = playingText === text;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPlay(text); }}
      aria-label={playing ? "Playing…" : "Play audio"}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-[filter] hover:brightness-110 ${className}`}
      style={{ background: "var(--violet-bg)", color: "var(--tag-violet)" }}
    >
      {playing ? (
        <svg className="w-3.5 h-3.5 animate-pulse" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M4 9h3l5-5v16l-5-5H4V9zm12.5 3a3.5 3.5 0 0 0-2-3.15v6.3a3.5 3.5 0 0 0 2-3.15z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7L8 5z" />
        </svg>
      )}
    </button>
  );
}

/**
 * ChoiceButton — an answer option with reveal states.
 * state: "idle" | "correct" | "wrong" | "dim"  (dim = revealed, not chosen)
 */
export function ChoiceButton({ children, state = "idle", disabled, onClick, arabic = false }) {
  const styles = {
    idle: { background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" },
    correct: { background: "rgba(52,168,133,0.18)", border: "1px solid var(--teal)", color: "var(--teal)" },
    wrong: { background: "rgba(220,90,90,0.15)", border: "1px solid #dc5a5a", color: "#e58c8c" },
    dim: { background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment-dim)", opacity: 0.6 },
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      dir={arabic ? "rtl" : undefined}
      lang={arabic ? "ar" : undefined}
      className={`w-full px-4 py-3 min-h-[48px] rounded-xl text-center transition-all hover:brightness-110 disabled:cursor-default ${
        arabic ? "font-arabic text-2xl" : "text-sm font-medium"
      }`}
      style={styles[state]}
    >
      {children}
    </button>
  );
}

/** Small progress chip, e.g. "9/12 explored". */
export function ProgressPill({ children }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-2xl px-3 py-1 text-[0.78rem] whitespace-nowrap"
      style={{ color: "var(--tag-violet)", border: "1px solid var(--panel-line)" }}
    >
      {children}
    </span>
  );
}

/** Section wrapper card. */
export function ActivityCard({ children }) {
  return (
    <section className="dark-scope surface-glass px-5 py-5 sm:px-7 sm:py-6" style={{ borderRadius: 14 }}>
      {children}
    </section>
  );
}

// One Arabic run: Arabic chars possibly joined by whitespace (never crossing
// Latin text). The single capture group makes String.split interleave runs.
const AR_RUN = /([؀-ۿ][؀-ۿ\s]*[؀-ۿ]|[؀-ۿ])/;

/**
 * MixedText — inline prose with embedded Arabic bumped to a legible size.
 * Diacritics (tanwīn, shadda, sukūn…) are unreadable at body-text scale, so
 * every Arabic run gets the Arabic font at text-xl while the Latin text
 * around it keeps the paragraph's own size.
 */
export function MixedText({ text }) {
  if (!text) return null;
  return String(text)
    .split(AR_RUN)
    .map((part, i) =>
      /[؀-ۿ]/.test(part) ? (
        <span key={i} lang="ar" className="font-arabic text-xl leading-[1.8]">{part}</span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
}

export function shuffled(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Shuffle a multiple-choice item's choices for display, tracking where the
 * correct answer landed. Lesson data authors the correct choice first
 * (correct_index is almost always 0 — easiest to write and review), so the
 * UI must never render choices in data order. Re-shuffles only when `deps`
 * change (new round / question / retake), never on mid-question re-renders.
 */
export function useShuffledChoices(choices, correctIndex, deps) {
  return useMemo(() => {
    const order = shuffled(choices.map((_, i) => i));
    return {
      choices: order.map((i) => choices[i]),
      correctIndex: order.indexOf(correctIndex),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
